import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { User } from '@sss/sdk';
import { sdk } from '../lib/sdk';
import { clearSession } from '../store/authSlice';
import ProfileTab from '../components/Settings/ProfileTab';
import SecurityTab from '../components/Settings/SecurityTab';
import AuthProvidersTab from '../components/Settings/AuthProvidersTab';
import DevicesTab from '../components/Settings/DevicesTab';
import DangerZoneTab from '../components/Settings/DangerZoneTab';
import ApiTokensTab from '../components/Settings/ApiTokensTab';
// @feature:oauthServer:start
import ConnectedAppsTab from '../components/Settings/ConnectedAppsTab';
// @feature:oauthServer:end
import VerifyEmailBanner from '../components/VerifyEmailBanner';

const TABS = [
  'profile',
  'security',
  'auth-providers',
  'api-tokens',
  // @feature:oauthServer:start
  'connected-apps',
  // @feature:oauthServer:end
  'devices',
  'danger-zone',
] as const;
type TabKey = (typeof TABS)[number];

export default function SettingsPage() {
  const { t } = useTranslation();
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [me, setMe] = useState<User | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const activeTab: TabKey = (TABS as readonly string[]).includes(tab || '')
    ? (tab as TabKey)
    : 'profile';

  useEffect(() => {
    sdk.users
      .me()
      .then(setMe)
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Failed to load user'));
  }, []);

  async function onSignOut() {
    dispatch(clearSession());
    navigate('/login');
  }

  if (loadError) return <main style={page}><p style={{ color: 'red' }}>{loadError}</p></main>;
  if (!me) return <main style={page}><p>{t('common.loading')}</p></main>;

  return (
    <main style={page}>
      <header style={header}>
        <h1 style={{ margin: 0 }}>{t('settings.title')}</h1>
        <div>
          <Link to="/account" style={linkStyle}>← {t('settings.back')}</Link>
          {' · '}
          <button onClick={onSignOut} style={btnLink}>{t('common.signOut')}</button>
        </div>
      </header>

      <nav style={tabNav} aria-label={t('settings.title')}>
        {TABS.map((key) => (
          <Link
            key={key}
            to={`/settings/${key}`}
            style={{ ...tabLink, ...(key === activeTab ? tabLinkActive : {}) }}
          >
            {t(`settings.tabs.${camel(key)}`)}
          </Link>
        ))}
      </nav>

      <VerifyEmailBanner email={me.email} visible={!me.isEmailVerified} />

      <section style={tabBody}>
        {activeTab === 'profile' && <ProfileTab me={me} onUpdated={setMe} />}
        {activeTab === 'security' && <SecurityTab me={me} onUpdated={setMe} />}
        {activeTab === 'auth-providers' && <AuthProvidersTab />}
        {activeTab === 'api-tokens' && <ApiTokensTab />}
        {/* @feature:oauthServer:start */}
        {activeTab === 'connected-apps' && <ConnectedAppsTab />}
        {/* @feature:oauthServer:end */}
        {activeTab === 'devices' && <DevicesTab />}
        {activeTab === 'danger-zone' && <DangerZoneTab me={me} onAccountDeleted={onSignOut} />}
      </section>
    </main>
  );
}

function camel(s: string): string {
  return s.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
}

const page: React.CSSProperties = { maxWidth: 720, margin: '2rem auto', padding: '0 1rem', fontFamily: 'system-ui, sans-serif' };
const header: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1.5rem' };
const tabNav: React.CSSProperties = { display: 'flex', gap: '0.5rem', borderBottom: '1px solid #ddd', marginBottom: '1.5rem' };
const tabLink: React.CSSProperties = { padding: '0.5rem 0.75rem', textDecoration: 'none', color: '#333', borderBottom: '2px solid transparent' };
const tabLinkActive: React.CSSProperties = { borderBottomColor: '#0066cc', fontWeight: 600 };
const tabBody: React.CSSProperties = { paddingTop: '0.5rem' };
const linkStyle: React.CSSProperties = { color: '#0066cc' };
const btnLink: React.CSSProperties = { background: 'none', border: 'none', color: '#0066cc', padding: 0, cursor: 'pointer', font: 'inherit' };
